import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { RSVP, Event, TicketType } from '@/types';
import { Ticket } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { normalizeTicketCoursesToFormatA } from '@/lib/ticket-courses';

export default function EditRSVPTicketPage() {
  const { unitId: unitId, eventId, rsvpId } = useParams<{ unitId: string; eventId: string; rsvpId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rsvp, setRsvp] = useState<RSVP | null>(null);
  const [event, setEvent] = useState<Event | null>(null);

  // Form state - tickets and meal preferences only
  const [ticketQuantities, setTicketQuantities] = useState<Record<string, number>>({});
  // ticketCourseSelections[ticketId][instanceIndex][courseId] = option
  const [ticketCourseSelections, setTicketCourseSelections] = useState<Record<string, Record<number, Record<string, string>>>>({});
  const [courseSelections, setCourseSelections] = useState<Record<string, string>>({});
  const [mealChoice, setMealChoice] = useState('');
  const [allergies, setAllergies] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!unitId || !eventId || !rsvpId) {
        showError('Invalid RSVP link');
        setLoading(false);
        return;
      }

      try {
        // Fetch RSVP
        const rsvpDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId));
        if (!rsvpDoc.exists()) {
          showError('RSVP not found');
          setLoading(false);
          return;
        }

        const rsvpData = rsvpDoc.data() as RSVP;
        
        // Check if user is authorized
        if (rsvpData.userId !== currentUser?.uid) {
          showError('You are not authorized to edit this RSVP');
          navigate('/dashboard');
          return;
        }

        // Fetch event
        const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
        if (!eventDoc.exists()) {
          showError('Event not found');
          setLoading(false);
          return;
        }

        const eventData = eventDoc.data();
        const eventDate = eventData.date?.toDate() || new Date();
        const eventObj: Event = {
          id: eventDoc.id,
          unitId,
          name: eventData.name,
          date: eventDate,
          location: eventData.location,
          costPerPerson: eventData.costPerPerson,
          questionnaire: eventData.questionnaire || [],
          courses: eventData.courses || [],
          tickets: eventData.tickets || [],
          maxCapacity: eventData.maxCapacity,
          currentAttendees: eventData.currentAttendees || 0,
          waitlistEnabled: eventData.waitlistEnabled || false,
          waitlist: eventData.waitlist || [],
          rsvpDeadline: eventData.rsvpDeadline?.toDate() || new Date(),
          paymentDeadline: eventData.paymentDeadline?.toDate() || eventDate,
          eventStatus: eventData.eventStatus,
          dresscode: eventData.dresscode,
          customInstructions: eventData.customInstructions,
          isPublic: eventData.isPublic ?? false,
          requirePrefixRankUnitInfo: eventData.requirePrefixRankUnitInfo ?? true,
          createdBy: eventData.createdBy,
          createdAt: eventData.createdAt?.toDate() || new Date(),
        };

        // Check if past deadline
        if (new Date() > eventObj.rsvpDeadline) {
          showError('The RSVP deadline has passed. You can no longer edit your registration.');
          setLoading(false);
          return;
        }

        setEvent(eventObj);
        setRsvp(rsvpData);
        
        // Load ticket selections
        if (rsvpData.responses?.Tickets && Array.isArray(rsvpData.responses.Tickets)) {
          const quantities: Record<string, number> = {};
          rsvpData.responses.Tickets.forEach((ticket: any) => {
            quantities[ticket.id] = ticket.qty || 1;
          });
          setTicketQuantities(quantities);
          
          if (rsvpData.responses.TicketCourses) {
            const normalizedTc = normalizeTicketCoursesToFormatA(
              rsvpData.responses.TicketCourses
            );
            if (Object.keys(normalizedTc).length > 0) {
              setTicketCourseSelections(normalizedTc);
            }
          }
        }
        
        // Load legacy course selections or meal choice
        if (eventObj.courses && eventObj.courses.length > 0) {
          const selections: Record<string, string> = {};
          eventObj.courses.forEach((course) => {
            selections[course.id] = rsvpData.responses?.[course.name] || '';
          });
          setCourseSelections(selections);
        } else {
          setMealChoice(rsvpData.responses?.Meal || '');
        }
        
        setAllergies(rsvpData.responses?.Allergies || '');

      } catch (error) {
        console.error('Error fetching data:', error);
        showError('Failed to load RSVP');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [unitId, eventId, rsvpId, currentUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!event || !unitId || !eventId || !rsvpId || !rsvp) {
      showError('Invalid RSVP');
      return;
    }

    // Validate tickets if using ticket system
    if (event.tickets && event.tickets.length > 0) {
      const totalTickets = Object.values(ticketQuantities).reduce((a, b) => a + (b || 0), 0);
      if (totalTickets <= 0) {
        showError('Please select at least one ticket');
        return;
      }
      
      // Validate per-ticket course selections
      for (const t of event.tickets) {
        const qty = ticketQuantities[t.id] || 0;
        if (qty > 0 && t.courses && t.courses.length > 0) {
          for (let i = 1; i <= qty; i++) {
            for (const c of t.courses) {
              const chosen = ticketCourseSelections?.[t.id]?.[i]?.[c.id];
              if (!chosen) {
                showError('Please select options for all courses for each ticket');
                return;
              }
            }
          }
        }
      }
    } else if (event.courses && event.courses.length > 0) {
      // Validate course selections
      const allCoursesSelected = event.courses.every(
        (course) => courseSelections[course.id]?.trim()
      );
      if (!allCoursesSelected) {
        showError('Please select an option for all courses');
        return;
      }
    } else if (!mealChoice) {
      showError('Please select your meal choice');
      return;
    }

    setSubmitting(true);
    try {
      // Preserve existing responses (questionnaire, etc.) and only update ticket/meal fields we edit
      const responses: Record<string, any> = {
        ...(rsvp.responses || {}),
        Allergies: allergies,
      };

      // Add ticket selections or course/meal selections
      if (event.tickets && event.tickets.length > 0) {
        const selectedTickets = event.tickets
          .map((t) => ({ id: t.id, name: t.name, price: t.price, qty: ticketQuantities[t.id] || 0 }))
          .filter((t) => t.qty > 0);
        responses.Tickets = selectedTickets;
        if (selectedTickets.length > 0) {
          const existingNormalized = normalizeTicketCoursesToFormatA(
            (rsvp.responses as any)?.TicketCourses
          );
          responses.TicketCourses = { ...existingNormalized, ...ticketCourseSelections };
        }
      } else if (event.courses && event.courses.length > 0) {
        event.courses.forEach((course) => {
          responses[course.name] = courseSelections[course.id];
        });
      } else {
        responses.Meal = mealChoice;
      }

      await updateDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId), {
        responses,
        updatedAt: new Date(),
      });

      showSuccess('Ticket and meal preferences updated successfully!');
      navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/dashboard`);
    } catch (error) {
      console.error('Error updating RSVP:', error);
      showError('Failed to update RSVP. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading RSVP...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event || !rsvp) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>RSVP Not Found</CardTitle>
              <CardDescription>The RSVP you're looking for doesn't exist or you don't have permission to edit it.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-3xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/dashboard`)}>
              ← Back to RSVP Dashboard
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="h-5 w-5" />
                Edit Ticket & Meal Preferences
              </CardTitle>
              <CardDescription>
                Update your ticket selection and meal preferences for {event.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Ticket Selection */}
                {event.tickets && event.tickets.length > 0 ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Select Tickets</h3>
                    <div className="space-y-4">
                      {event.tickets.map((t) => (
                        <div key={t.id} className="rounded-lg border p-4 space-y-3">
                          <div className="grid gap-4 md:grid-cols-3 items-end">
                            <div className="space-y-2">
                              <Label>Ticket</Label>
                              <div className="rounded-md border px-3 py-2 text-sm">{t.name}</div>
                            </div>
                            <div className="space-y-2">
                              <Label>Price</Label>
                              <div className="rounded-md border px-3 py-2 text-sm">{formatCurrency(t.price || 0)}</div>
                            </div>
                            <div className="space-y-2">
                              <Label>Quantity</Label>
                              <Input
                                type="number"
                                min={0}
                                value={ticketQuantities[t.id] ?? 0}
                                onChange={(e) => {
                                  const qty = Math.max(0, parseInt(e.target.value || '0'));
                                  setTicketQuantities({ ...ticketQuantities, [t.id]: qty });
                                }}
                              />
                            </div>
                          </div>
                          {/* Per-ticket course selections */}
                          {(() => {
                            const qty = ticketQuantities[t.id] || 0;
                            if (!t.courses || t.courses.length === 0 || qty <= 0) return null;
                            const blocks = [] as any[];
                            for (let i = 1; i <= qty; i++) {
                              blocks.push(
                                <div key={`${t.id}-${i}`} className="rounded-md border p-3">
                                  <div className="font-medium mb-2">{t.name} - Ticket {i}</div>
                                  {t.courses.map((c) => (
                                    <div key={c.id} className="space-y-2 mb-3">
                                      <Label>{c.name} *</Label>
                                      <Select
                                        value={ticketCourseSelections?.[t.id]?.[i]?.[c.id] || ''}
                                        onValueChange={(value) => {
                                          setTicketCourseSelections((prev) => {
                                            const existingTicket = prev[t.id] || {};
                                            const existingInstance = existingTicket[i] || {};
                                            return {
                                              ...prev,
                                              [t.id]: {
                                                ...existingTicket,
                                                [i]: { ...existingInstance, [c.id]: value },
                                              },
                                            };
                                          });
                                        }}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select your choice..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {(c.options || []).map((option) => (
                                            <SelectItem key={option} value={option}>
                                              {option}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            return <div className="space-y-3">{blocks}</div>;
                          })()}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* Meal Preferences - Legacy */}
                {!event.tickets || event.tickets.length === 0 ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Meal Preferences</h3>

                    {event.courses && event.courses.length > 0 ? (
                      // Show course-based selections
                      event.courses.map((course) => (
                        <div key={course.id} className="space-y-2">
                          <Label htmlFor={`course-${course.id}`}>{course.name} *</Label>
                          <Select
                            value={courseSelections[course.id] || ''}
                            onValueChange={(value) =>
                              setCourseSelections({
                                ...courseSelections,
                                [course.id]: value,
                              })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select your choice..." />
                            </SelectTrigger>
                            <SelectContent>
                              {course.options.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))
                    ) : (
                      // Fallback to traditional meal choice
                      <div className="space-y-2">
                        <Label htmlFor="mealChoice">Meal Choice *</Label>
                        <Select value={mealChoice} onValueChange={setMealChoice}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your meal..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Beef">Beef Wellington</SelectItem>
                            <SelectItem value="Salmon">Salmon Fillet</SelectItem>
                            <SelectItem value="Vegetarian">Vegetarian Option</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ) : null}

                {/* Allergies */}
                <div className="space-y-2">
                  <Label htmlFor="allergies">Allergies & Special Requirements</Label>
                  <Textarea
                    id="allergies"
                    placeholder="Please list any allergies or special dietary requirements..."
                    rows={3}
                    value={allergies}
                    onChange={(e) => setAllergies(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    This information will be shared with the kitchen staff to ensure your safety
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/dashboard`)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Updating...' : 'Update Ticket & Meals'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

